/* eslint-disable react-hooks/exhaustive-deps */
import { useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * Parses URL search params into a filter state object.
 * Array values are stored as comma-separated strings in the URL.
 */
const parseFiltersFromUrl = (searchParams) => {
    const filters = {};

    const status = searchParams.get('status');
    if (status) filters.status = status;

    const search = searchParams.get('search');
    if (search) filters.search = search;

    const treatment = searchParams.get('treatment');
    if (treatment) filters.treatment = treatment.split(',');

    const assignee = searchParams.get('assignee');
    if (assignee) filters.assignee = assignee.split(',');

    const tags = searchParams.get('tags');
    if (tags) filters.tags = tags.split(',');

    const referral = searchParams.get('referral');
    if (referral) filters.referral = referral;

    const startDate = searchParams.get('startDate');
    if (startDate) filters.startDate = startDate;

    const endDate = searchParams.get('endDate');
    if (endDate) filters.endDate = endDate;

    const depositPaid = searchParams.get('depositPaid');
    if (depositPaid) filters.depositPaid = depositPaid;

    const archiveReason = searchParams.get('archiveReason');
    if (archiveReason) filters.archiveReason = archiveReason;

    const bookedWith = searchParams.get('bookedWith');
    if (bookedWith) filters.bookedWith = bookedWith.split(',');

    const consultationBookedWith = searchParams.get('consultationBookedWith');
    if (consultationBookedWith) filters.consultationBookedWith = consultationBookedWith.split(',');

    const noTreatment = searchParams.get('noTreatment');
    if (noTreatment === 'true') filters.noTreatment = true;

    // Launchpad deep-link params — consumed once on mount by the target page;
    // not written back via syncToUrl (the page clears them naturally by not
    // including them in subsequent syncToUrl calls).
    // viewMode: 'tasks' | 'contacts' — overrides localStorage default in NewLead.
    // bucket: 'overdue' | 'due_today' | 'due_now' | ... — pre-selects a task bucket.
    const viewMode = searchParams.get('viewMode');
    if (viewMode) filters.viewMode = viewMode;

    const bucket = searchParams.get('bucket');
    if (bucket) filters.bucket = bucket;

    // noNextAction: 'true' — pre-applies the no_next_action filter on Attended.
    const noNextAction = searchParams.get('noNextAction');
    if (noNextAction === 'true') filters.noNextAction = true;

    return filters;
};

/**
 * Set of URL keys this hook owns. Anything else in the query string is
 * considered foreign and must be preserved across syncToUrl writes — otherwise
 * `setSearchParams(..., { replace: true })` clobbers it.
 *
 * If you add a new filter key to parseFiltersFromUrl / serializeFiltersToUrl
 * below, you MUST add it here too or syncToUrl will treat it as foreign and
 * carry it forward forever.
 */
const FILTER_KEYS = new Set([
    'status', 'search', 'treatment', 'assignee', 'tags', 'referral',
    'startDate', 'endDate', 'depositPaid', 'archiveReason',
    'bookedWith', 'consultationBookedWith', 'noTreatment',
    // Launchpad deep-link params — must be in FILTER_KEYS so syncToUrl strips
    // them on the first user-driven filter change rather than carrying them
    // forward forever as "foreign" params.
    'viewMode', 'bucket', 'noNextAction',
]);

/**
 * Serializes a filter state object into URL search params.
 * Omits empty/default values to keep URLs clean.
 */
const serializeFiltersToUrl = (filterState) => {
    const params = new URLSearchParams();

    if (filterState.status) params.set('status', filterState.status);
    if (filterState.search) params.set('search', filterState.search);

    if (filterState.treatment?.length) {
        const ids = filterState.treatment.map((t) => (typeof t === 'object' ? t.id : t));
        params.set('treatment', ids.join(','));
    }

    if (filterState.assignee?.length) {
        const ids = filterState.assignee.map((a) => (typeof a === 'object' ? a.id : a));
        params.set('assignee', ids.join(','));
    }

    if (filterState.tags?.length) {
        const names = filterState.tags.map((t) => (typeof t === 'object' ? t.name : t));
        params.set('tags', names.join(','));
    }

    if (filterState.referral) params.set('referral', filterState.referral);
    if (filterState.startDate) params.set('startDate', filterState.startDate);
    if (filterState.endDate) params.set('endDate', filterState.endDate);
    if (filterState.depositPaid) params.set('depositPaid', filterState.depositPaid);
    if (filterState.archiveReason) params.set('archiveReason', filterState.archiveReason);

    if (filterState.bookedWith?.length) {
        const ids = filterState.bookedWith.map((b) => (typeof b === 'object' ? b.id : b));
        params.set('bookedWith', ids.join(','));
    }

    if (filterState.consultationBookedWith?.length) {
        const ids = filterState.consultationBookedWith.map((c) => (typeof c === 'object' ? c.id : c));
        params.set('consultationBookedWith', ids.join(','));
    }

    if (filterState.noTreatment) params.set('noTreatment', 'true');

    return params;
};

/**
 * Hook that syncs table filter state bidirectionally with URL search params.
 *
 * @returns {{ initialFilters: object, syncToUrl: function, hasUrlFilters: boolean }}
 */
const useTableQueryParams = () => {
    const [searchParams, setSearchParams] = useSearchParams();

    const initialFilters = useMemo(() => parseFiltersFromUrl(searchParams), []);

    const hasUrlFilters = useMemo(() => Object.keys(initialFilters).length > 0, [initialFilters]);

    const syncToUrl = useCallback((filterState, { clearExtra = [] } = {}) => {
        // Preserve non-filter params — setSearchParams with { replace: true } writes
        // the whole query string, so anything not re-emitted by serializeFiltersToUrl
        // would otherwise be stripped.
        // clearExtra: extra keys to strip even if not in FILTER_KEYS (e.g. 'tab',
        // 'checklist_state' when a search clears all tab-scoped filters).
        const preserved = new URLSearchParams();
        searchParams.forEach((value, key) => {
            if (!FILTER_KEYS.has(key) && !clearExtra.includes(key)) preserved.append(key, value);
        });

        if (!filterState || Object.keys(filterState).length === 0) {
            setSearchParams(preserved, { replace: true });
            return;
        }
        const params = serializeFiltersToUrl(filterState);
        preserved.forEach((value, key) => {
            if (!params.has(key)) params.append(key, value);
        });
        setSearchParams(params, { replace: true });
    }, [setSearchParams, searchParams]);

    return { initialFilters, syncToUrl, hasUrlFilters };
};

export default useTableQueryParams;
